import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Reusable server-side pagination bar.
 *
 * Props:
 *  - page (1-based), total, limit, totalPages
 *  - onPageChange(nextPage), onLimitChange(nextLimit)
 *  - pageSizeOptions (default [10, 25, 50, 100])
 */
export function PaginationBar({
  page = 1, total = 0, limit = 10, totalPages = 1,
  onPageChange, onLimitChange,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  if (total === 0) return null;

  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const pageNumbers = buildPageNumbers(page, totalPages);
  const atFirst = page <= 1;
  const atLast = page >= totalPages;

  return (
    <div
      className="flex items-center justify-between gap-4 flex-wrap py-3 text-sm"
      data-testid="pagination-bar"
    >
      <div className="text-muted-foreground" data-testid="pagination-summary">
        Showing <span className="font-medium text-foreground">{from}–{to}</span>{" "}
        of <span className="font-medium text-foreground">{total.toLocaleString("en-IN")}</span> records
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 mr-2">
          <span className="text-muted-foreground">Rows</span>
          <Select value={String(limit)} onValueChange={(v) => onLimitChange?.(Number(v))}>
            <SelectTrigger className="w-[80px] h-8" data-testid="pagination-limit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button size="sm" variant="outline"
          disabled={atFirst} onClick={() => onPageChange?.(1)}
          className="h-8 w-8 p-0" data-testid="pagination-first" title="First">
          <ChevronsLeft className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="outline"
          disabled={atFirst} onClick={() => onPageChange?.(page - 1)}
          className="h-8 w-8 p-0" data-testid="pagination-prev" title="Previous">
          <ChevronLeft className="w-3 h-3" />
        </Button>

        {pageNumbers.map((n, i) =>
          n === "…" ? (
            <span key={`e-${i}`} className="px-2 text-muted-foreground">…</span>
          ) : (
            <Button
              key={n}
              size="sm"
              variant={n === page ? "default" : "outline"}
              onClick={() => onPageChange?.(n)}
              className="h-8 min-w-[32px] px-2"
              data-testid={`pagination-page-${n}`}
            >
              {n}
            </Button>
          )
        )}

        <Button size="sm" variant="outline"
          disabled={atLast} onClick={() => onPageChange?.(page + 1)}
          className="h-8 w-8 p-0" data-testid="pagination-next" title="Next">
          <ChevronRight className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="outline"
          disabled={atLast} onClick={() => onPageChange?.(totalPages)}
          className="h-8 w-8 p-0" data-testid="pagination-last" title="Last">
          <ChevronsRight className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Compact page list with ellipses:
 *   1 … 4 5 [6] 7 8 … 48
 */
function buildPageNumbers(current, totalPages) {
  const pages = [];
  const window = 1; // pages either side of current
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }
  pages.push(1);
  const start = Math.max(2, current - window);
  const end = Math.min(totalPages - 1, current + window);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push("…");
  pages.push(totalPages);
  return pages;
}
